import * as React from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import * as Tooltip from '@radix-ui/react-tooltip';

import {
  useMonkeysComponentAttributes,
  useMonkeysLocale,
} from '../../provider/context';

function cn(...inputs: (string | undefined | null | false)[]): string {
  return inputs.filter(Boolean).join(' ');
}

// ---------- Inline SVG Icons ----------

function SunIcon({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2" /><path d="M12 20v2" />
      <path d="m4.93 4.93 1.41 1.41" /><path d="m17.66 17.66 1.41 1.41" />
      <path d="M2 12h2" /><path d="M20 12h2" />
      <path d="m6.34 17.66-1.41 1.41" /><path d="m19.07 4.93-1.41 1.41" />
    </svg>
  );
}

function MoonIcon({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />
    </svg>
  );
}

function MonitorIcon({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <rect width="20" height="14" x="2" y="3" rx="2" />
      <line x1="8" x2="16" y1="21" y2="21" />
      <line x1="12" x2="12" y1="17" y2="21" />
    </svg>
  );
}

// ---------- Component ----------

export type DarkModeValue = 'auto' | 'light' | 'dark';

export interface DarkModeSelectorProps {
  mode: DarkModeValue;
  onModeChange: (mode: DarkModeValue) => void;
  labels?: { auto?: string; light?: string; dark?: string };
  tooltip?: string;
  className?: string;
}

const triggerIconMap: Record<DarkModeValue, React.FC<{ className?: string }>> = {
  light: SunIcon,
  dark: MoonIcon,
  auto: MonitorIcon,
};

export function DarkModeSelector({
  mode,
  onModeChange,
  labels = {},
  tooltip,
  className,
}: DarkModeSelectorProps) {
  const locale = useMonkeysLocale();
  const componentAttributes = useMonkeysComponentAttributes();
  const localeLabels = locale.messages.toolbar.theme;
  const TriggerIcon = triggerIconMap[mode];
  const resolvedLabels = {
    auto: labels.auto ?? localeLabels.auto,
    light: labels.light ?? localeLabels.light,
    dark: labels.dark ?? localeLabels.dark,
  };

  const options: { value: DarkModeValue; label: string; Icon: React.FC<{ className?: string }> }[] = [
    { value: 'auto', label: resolvedLabels.auto, Icon: MonitorIcon },
    { value: 'light', label: resolvedLabels.light, Icon: SunIcon },
    { value: 'dark', label: resolvedLabels.dark, Icon: MoonIcon },
  ];

  const trigger = (
    <DropdownMenu.Trigger asChild>
      <button
        {...componentAttributes}
        className={cn(
          'inline-flex h-8 w-8 items-center justify-center rounded-md text-[hsl(var(--foreground))] hover:bg-[hsl(var(--accent))] hover:text-[hsl(var(--accent-foreground))] transition-colors focus-visible:outline-none',
          className,
        )}
      >
        <TriggerIcon />
        <span className="sr-only">{tooltip ?? localeLabels.toggle}</span>
      </button>
    </DropdownMenu.Trigger>
  );

  return (
    <DropdownMenu.Root>
      {tooltip ? (
        <Tooltip.Provider delayDuration={300}>
          <Tooltip.Root>
            <Tooltip.Trigger asChild>{trigger}</Tooltip.Trigger>
            <Tooltip.Portal>
              <Tooltip.Content
                className="rounded-md bg-[hsl(var(--popover))] px-3 py-1.5 text-xs text-[hsl(var(--popover-foreground))] shadow-md border border-[hsl(var(--border))] animate-in fade-in-0 zoom-in-95"
                sideOffset={5}
              >
                {tooltip}
                <Tooltip.Arrow className="fill-[hsl(var(--popover))]" />
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>
        </Tooltip.Provider>
      ) : (
        trigger
      )}

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className="z-50 min-w-[8rem] rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--popover))] p-1 text-[hsl(var(--popover-foreground))] shadow-md animate-in fade-in-0 zoom-in-95"
          sideOffset={5}
          align="end"
        >
          {options.map(({ value, label, Icon }) => (
            <DropdownMenu.Item
              key={value}
              className={cn(
                'relative flex cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-[hsl(var(--accent))] hover:text-[hsl(var(--accent-foreground))]',
                mode === value && 'bg-[hsl(var(--accent))]',
              )}
              onSelect={() => onModeChange(value)}
            >
              <Icon className="h-4 w-4" />
              {label}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

// ---------- Sub-menu variant (for embedding in user dropdown) ----------

function SunMoonIcon({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M12 8a2.83 2.83 0 0 0 4 4 4 4 0 1 1-4-4" />
      <path d="M12 2v2" /><path d="M12 20v2" />
      <path d="m4.9 4.9 1.4 1.4" /><path d="m17.7 17.7 1.4 1.4" />
      <path d="M2 12h2" /><path d="M20 12h2" />
      <path d="m6.3 17.7-1.4 1.4" /><path d="m19.1 4.9-1.4 1.4" />
    </svg>
  );
}

export interface DarkModeSubMenuProps {
  mode: DarkModeValue;
  onModeChange: (mode: DarkModeValue) => void;
  label?: string;
  labels?: { auto?: string; light?: string; dark?: string };
}

/**
 * A DropdownMenuSub component for embedding dark mode selector
 * inside a Radix DropdownMenu (e.g. user avatar menu).
 * Must be rendered as a child of DropdownMenu.Group / DropdownMenu.Content.
 */
export function DarkModeSubMenu({
  mode,
  onModeChange,
  label,
  labels = {},
}: DarkModeSubMenuProps) {
  const locale = useMonkeysLocale();
  const componentAttributes = useMonkeysComponentAttributes();
  const localeLabels = locale.messages.toolbar.theme;
  const fallbackLabels = locale.provided
    ? localeLabels
    : {
        label: '外观',
        auto: '跟随系统',
        light: '浅色模式',
        dark: '深色模式',
      };
  const resolvedLabels = {
    auto: labels.auto ?? fallbackLabels.auto,
    light: labels.light ?? fallbackLabels.light,
    dark: labels.dark ?? fallbackLabels.dark,
  };

  const currentLabel = mode === 'auto' ? resolvedLabels.auto : mode === 'dark' ? resolvedLabels.dark : resolvedLabels.light;

  const options: { value: DarkModeValue; label: string; Icon: React.FC<{ className?: string }> }[] = [
    { value: 'auto', label: resolvedLabels.auto, Icon: MonitorIcon },
    { value: 'light', label: resolvedLabels.light, Icon: SunIcon },
    { value: 'dark', label: resolvedLabels.dark, Icon: MoonIcon },
  ];

  return (
    <DropdownMenu.Sub>
      <DropdownMenu.SubTrigger
        {...componentAttributes}
        className="relative flex cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-[hsl(var(--accent))] hover:text-[hsl(var(--accent-foreground))] data-[state=open]:bg-[hsl(var(--accent))]"
      >
        <SunMoonIcon className="h-4 w-4" />
        <span>{label ?? fallbackLabels.label}</span>
        <span className="ml-auto text-xs text-[hsl(var(--muted-foreground))]">{currentLabel}</span>
      </DropdownMenu.SubTrigger>
      <DropdownMenu.Portal>
        <DropdownMenu.SubContent
          className="z-50 min-w-[8rem] rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--popover))] p-1 text-[hsl(var(--popover-foreground))] shadow-md animate-in fade-in-0 zoom-in-95"
          sideOffset={8}
        >
          {options.map(({ value, label: optLabel, Icon }) => (
            <DropdownMenu.CheckboxItem
              key={value}
              checked={mode === value}
              disabled={mode === value}
              className={cn(
                'relative flex cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 pl-7 text-sm outline-none transition-colors hover:bg-[hsl(var(--accent))] hover:text-[hsl(var(--accent-foreground))] data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
              )}
              onCheckedChange={() => onModeChange(value)}
            >
              <DropdownMenu.ItemIndicator className="absolute left-2 inline-flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
              </DropdownMenu.ItemIndicator>
              <Icon className="h-4 w-4" />
              {optLabel}
            </DropdownMenu.CheckboxItem>
          ))}
        </DropdownMenu.SubContent>
      </DropdownMenu.Portal>
    </DropdownMenu.Sub>
  );
}
